import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { flowUseCase } from "../../../util/bookingFlowUseCase";
import { isRoomFamily, UseCase } from "../../../util/useCases";

/**
 * What the room-family screens have always rendered while the flow's own use
 * case is not known yet (the first render, before `useBookingFlowLifecycle`
 * has set it): the apartment variant. Kept as it was by the W0-1 sweep — a
 * hall flow resolves to `hall` here once hall is live, not to this.
 */
const BEFORE_THE_FLOW_IS_KNOWN: UseCase = "apartment";

/**
 * Which room-family use case the CURRENT booking flow is — read from the flow
 * itself.
 *
 * ── Why not `sessionStorage`, which is what this replaces ─────────────────
 *
 * Three components inside the booking flow asked
 * `useServiceStorage("selectedService", ServiceTypeSelectorOption.APARTMENT)`
 * what flow they were in: the venue overview, the shared object card and the
 * footer's Next action. Two things made that wrong, and QA #2764 E2 hit both
 * at once on a venue link opened in a fresh tab:
 *
 * 1. **The default is `APARTMENT`, inside venue components.** `sessionStorage`
 *    is per-tab and empty in a new one, so a venue deep link rendered the
 *    apartment variant — "0 Betten · 0 Bäder" and "Jetzt buchen" on a meeting
 *    room.
 * 2. **`useServiceStorage` snapshots into `useState` once** and never re-reads,
 *    so the shell writing the right value on mount afterwards cannot correct
 *    it. The screen stays wrong for as long as it is open.
 *
 * `bookingCreation.useCase` is set by `useBookingFlowLifecycle` at the dialog
 * that owns the flow, so it is per-flow rather than per-tab, and it is a Redux
 * read — if it arrives a render late the component simply re-renders with the
 * right answer instead of being stuck with the wrong one.
 *
 * `selectedService` in `sessionStorage` keeps its real job: remembering which
 * service the customer last looked at, for the ORDERS screens. That is a
 * per-tab preference. Which flow a booking page belongs to is not.
 */
export const useBookingFlowService = (): UseCase => {
  const useCase = useSelector(
    (state: RootState) => state.bookingCreation.useCase
  );

  // `room` is the Redux name for the apartment flow — `flowUseCase` is the one
  // place that translates it. Callers ask a property of the result
  // (`spaceOf`), never compare its name.
  const flow = flowUseCase(useCase);
  return flow !== null && isRoomFamily(flow) ? flow : BEFORE_THE_FLOW_IS_KNOWN;
};
